// API client for backend integration
import { checkReceiptBeforeWrite } from "./receipt-duplicate-check";
import { trackApiSuccess } from "./analytics";
import { isSupplierFinancialEndpoint } from "./supplier-financial-cache-policy";
import {
  readCachedResponse,
  writeCachedResponse,
  enqueueMutation,
  isOnline,
  registerReplayer,
  describeEndpoint,
  isPersonalWasteTemplateEndpoint,
  type QueueItem,
} from "./offline";
import { authenticatedCacheScope } from "./auth-cache-scope";
import type { SupplierStatement } from "@shared/supplier-statement";
import type { ReceiptBrandOptions } from "@/lib/receipt-brands";

const API_BASE = "/api";

export type SupplierReceiptConfirmation = {
  cashBoxId?: string;
  payFromCashBox: boolean;
  cashPaymentAmount: string;
  expectedRevision?: number;
  paymentMethod?: "cash" | "credit";
  paymentTerms?: string;
  resolvedPaymentTerms?: string;
  dueDate?: string;
  cashTakingId?: string;
  cashExtraPaymentChoice?: "box" | "owed";
};

export interface SafetyStockUploadResult {
  created: string[];
  updated: string[];
  unchanged: string[];
  failed: { row: number; sku: string; reason: string }[];
}

export interface BranchBulkUploadResult {
  created: { rowNumber: number; name: string; id: string }[];
  skipped: { rowNumber: number; name: string; reason: string }[];
  failed: { rowNumber: number; name: string; reason: string }[];
}

export type SupplierPaymentBulkRowStatus = "valid" | "error" | "duplicate" | "invalid" | "ready";

export interface SupplierPaymentBulkPreviewRow {
  rowNumber: number;
  status?: SupplierPaymentBulkRowStatus;
  valid?: boolean;
  supplierName?: string | null;
  supplier?: { id?: string; name?: string } | null;
  supplierReference?: string | null;
  paymentDate?: string | null;
  amount?: string | number | null;
  paymentAmount?: string | number | null;
  method?: string | null;
  reference?: string | null;
  allocationAmount?: string | number | null;
  allocatedAmount?: string | number | null;
  creditAmount?: string | number | null;
  unallocatedAmount?: string | number | null;
  allocations?: Array<{ receiptNumber?: string; amount: string | number }>;
  reason?: string;
  reasons?: string[];
  errors?: string[];
}

export interface SupplierPaymentBulkPreview {
  importId?: string;
  previewToken: string;
  rows: SupplierPaymentBulkPreviewRow[];
  valid?: number;
  errors?: number;
  duplicates?: number;
  summary?: {
    totalRows?: number;
    total?: number;
    valid?: number;
    validRows?: number;
    errors?: number;
    invalid?: number;
    errorRows?: number;
    duplicates?: number;
    duplicateRows?: number;
  };
}

export interface SupplierPaymentBulkOutcome {
  rowNumber: number;
  status?: "created" | "skipped" | "failed";
  supplierName?: string | null;
  reference?: string | null;
  paymentId?: string;
  reason?: string;
  errors?: string[];
}

export interface SupplierPaymentBulkResult {
  importId?: string;
  created?: SupplierPaymentBulkOutcome[] | number;
  skipped?: SupplierPaymentBulkOutcome[] | number;
  failed?: SupplierPaymentBulkOutcome[] | number;
  outcomes?: SupplierPaymentBulkOutcome[];
  rows?: SupplierPaymentBulkOutcome[];
  summary?: { created?: number; skipped?: number; failed?: number };
}

export interface OpeningStockOutcome {
  row: number;
  branch: string;
  sku: string;
  quantity: number | null;
  unitCost: number | null;
  locationId: string | null;
  itemId: string | null;
  itemName: string | null;
  locationName: string | null;
  status: "applied" | "skipped" | "error";
  reason?: string;
}

export interface OpeningStockUploadResult {
  uploadId: string;
  mode: "skip_existing" | "replace";
  applied: number;
  skipped: number;
  errors: number;
  status: "applied" | "partial" | "failed";
  byBranch: { branch: string; applied: number; skipped: number; error: number }[];
  outcomes: OpeningStockOutcome[];
}

export type CategoryScope = "general" | "finished_product";

export interface ItemPurchasingCost {
  itemId: string;
  wac: number | null;
  receiptUnit: string | null;
}

export interface SupplierItemPrice {
  item_id: string;
  item_name: string;
  item_sku: string | null;
  latest_price: string | number | null;
  latest_unit: string | null;
  last_receipt_date: string | null;
  last_receipt_id: string | null;
  last_receipt_line_id: string | null;
  cheapest_price: string | number | null;
  cheapest_supplier_name: string | null;
  cheapest_supplier_id: string | null;
  cheapest_receipt_date: string | null;
  cheapest_receipt_id: string | null;
  cheapest_receipt_line_id: string | null;
  diff_pct: string | number | null;
  // includeFallback=true consumers use these when this supplier has no receipt.
  fallback_price: string | number | null;
  fallback_unit: string | null;
  fallback_receipt_date: string | null;
  fallback_supplier_id: string | null;
  fallback_supplier_name: string | null;
}

// Helper to safely convert Date objects to ISO strings before JSON serialization
// This avoids issues with Date.prototype.toISOString being corrupted or Date-like objects
function safeSerialize(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle Date objects (including date-fns returned dates)
  if (obj instanceof Date) {
    if (isNaN(obj.getTime())) {
      return null;
    }
    // Build ISO string manually to avoid prototype issues
    const year = obj.getUTCFullYear();
    const month = String(obj.getUTCMonth() + 1).padStart(2, '0');
    const day = String(obj.getUTCDate()).padStart(2, '0');
    const hours = String(obj.getUTCHours()).padStart(2, '0');
    const minutes = String(obj.getUTCMinutes()).padStart(2, '0');
    const seconds = String(obj.getUTCSeconds()).padStart(2, '0');
    const ms = String(obj.getUTCMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
  }
  
  // Handle Date-like objects that have getTime but aren't instanceof Date
  if (typeof obj === 'object' && obj !== null && 'getTime' in obj && typeof (obj as any).getTime === 'function') {
    try {
      const time = (obj as any).getTime();
      if (typeof time === 'number' && !isNaN(time)) {
        const d = new Date(time);
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        const hours = String(d.getUTCHours()).padStart(2, '0');
        const minutes = String(d.getUTCMinutes()).padStart(2, '0');
        const seconds = String(d.getUTCSeconds()).padStart(2, '0');
        const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
      }
    } catch {
      // Fall through to regular object handling
    }
  }
  
  if (Array.isArray(obj)) {
    return obj.map(safeSerialize);
  }
  
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = safeSerialize(value);
    }
    return result;
  }
  return obj;
}

// Safely stringify data, handling Date objects that may have corrupted prototypes
function safeStringify(data: unknown): string {
  return JSON.stringify(safeSerialize(data));
}

export type ExternalInventoryOverrideStatus =
  | "applied"
  | "failed"
  | "not_applicable";

// Endpoints that should never be served from cache or queued — auth,
// session, and anything that must hit the live server.
function isCacheableGet(endpoint: string): boolean {
  if (endpoint.startsWith("/expense-setup")) return false;
  if (isPersonalWasteTemplateEndpoint(endpoint)) return false;
  // PO edit/action baselines must never come from persistent offline storage.
  if (/^\/purchase-orders(?:\/|\?|$)/.test(endpoint)) return false;
  if (/^\/public\/supplier-portal(?:\/|\?|$)/.test(endpoint)) return false;
  if (isSupplierFinancialEndpoint(endpoint)) return false;
  if (endpoint.startsWith("/auth/")) return false;
  // Purchasing costs are identity/permission scoped financial data and must
  // always be refreshed from the server rather than the persistent cache.
  if (endpoint.startsWith("/items/purchasing-costs")) return false;
  // Sales upload previews/history and theoretical consumption are scoped,
  // auditable operational data. Do not persist them in the shared offline cache.
  if (
    endpoint.startsWith("/sales-upload/")
    || endpoint.startsWith("/reports/consumption")
    || endpoint.startsWith("/reports/sales-usage")
  ) return false;
  // Recipe overview and detail responses contain permission- and
  // managed-location-scoped costs. The persistent offline cache is not
  // identity-scoped, so it must never retain or serve these responses.
  const path = endpoint.split("?")[0].replace(/\/+$/, "") || "/";
  if (
    path === "/recipes"
    || path === "/recipes/overview"
    || path === "/recipes/drafts"
    || /^\/recipes\/[^/]+\/changes$/.test(path)
    || /^\/recipes\/[^/]+$/.test(path)
    || /^\/recipes\/[^/]+\/ingredient-receipts$/.test(path)
    || /^\/recipes\/[^/]+\/ingredient-replenishment-orders$/.test(path)
    || /^\/recipes\/[^/]+\/related-documents$/.test(path)
    // This report contains a scoped absence claim. It must always be fetched
    // for the active identity/workspace instead of being served from the
    // workspace-blind persistent offline cache.
    || path === "/reports/ingredients-review"
  ) return false;
  return true;
}
function isQueueableMutation(endpoint: string): boolean {
  if (endpoint.startsWith("/expense-setup")) return false;
  if (isPersonalWasteTemplateEndpoint(endpoint)) return false;
  if (/^\/public\/supplier-portal(?:\/|\?|$)/.test(endpoint)) return false;
  if (endpoint.startsWith("/auth/")) return false;
  // Sales confirmations are intentionally live-only: a reviewed preview can go
  // stale and must never be replayed after reconnecting. Keep the entire sales
  // workflow live so preview/void state is not deferred either.
  if (endpoint.startsWith("/sales-upload/")) return false;
  if (endpoint.startsWith("/push/")) return false;
  if (endpoint.startsWith("/supplier-receipts/pending-pricing")) return false;
  if (endpoint.startsWith("/supplier-payments")) return false;
  // Version transitions require fresh, explicit confirmation, not delayed replay.
  if (
    /^\/recipes\/[^/?]+\/(?:cancel-activation|activate|discard)(?:\?|$)/.test(endpoint)
    || /^\/recipes\/bulk-(?:activate|discard)(?:\?|$)/.test(endpoint)
    || /^\/recipes\/bulk-upload\/(?:preview|confirm)(?:\?|$)/.test(endpoint)
    || /^\/recipes\/drafts\/cleanup-unchanged(?:\?|$)/.test(endpoint)
  ) return false;
  return true;
}

export class OfflineQueuedError extends Error {
  status = 0;
  queued = true;
  constructor(message = "Saved offline — will sync when you reconnect.") {
    super(message);
    this.name = "OfflineQueuedError";
  }
}

export async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  const requestScope = authenticatedCacheScope();
  // Auto-attach the current workspace id so callers that use bare paths
  // (e.g. internal-market endpoints guarded by `requireWorkspaceAccess`)
  // don't need to thread workspaceId through every call site.
  const wsId = (() => {
    try { return localStorage.getItem("currentWorkspaceId") || ""; } catch { return ""; }
  })();
  const extraHeaders: Record<string, string> = {};
  if (wsId && !endpoint.includes("workspaceId=")) {
    extraHeaders["x-workspace-id"] = wsId;
  }
  const method = (options.method || "GET").toUpperCase();
  const isGet = method === "GET";

  // Offline pre-check: serve cached GETs, queue mutations, otherwise fail loud.
  if (typeof navigator !== "undefined" && !isOnline()) {
    if (isGet && isCacheableGet(endpoint)) {
      const cached = await readCachedResponse(endpoint, requestScope);
      if (cached) return cached.data;
      const err: any = new Error("You're offline and this page isn't cached yet.");
      err.status = 0;
      err.offline = true;
      throw err;
    }
    if (!isGet && isQueueableMutation(endpoint)) {
      await enqueueMutation({
        endpoint,
        method,
        body: options.body ? safeParseBody(options.body) : undefined,
        workspaceId: wsId || undefined,
        label: describeEndpoint(endpoint, method),
      });
      throw new OfflineQueuedError();
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...extraHeaders,
        ...options.headers,
      },
      credentials: "include", // Important for session cookies
    });
  } catch (networkErr: any) {
    // Network blew up mid-request. Treat like offline: serve cache for GETs,
    // queue mutations, otherwise rethrow.
    if (isGet && isCacheableGet(endpoint)) {
      const cached = await readCachedResponse(endpoint, requestScope);
      if (cached) return cached.data;
    } else if (!isGet && isQueueableMutation(endpoint)) {
      await enqueueMutation({
        endpoint,
        method,
        body: options.body ? safeParseBody(options.body) : undefined,
        workspaceId: wsId || undefined,
        label: describeEndpoint(endpoint, method),
      });
      throw new OfflineQueuedError();
    }
    const err: any = new Error("Network error — please check your connection and try again.");
    err.status = 0;
    err.offline = true;
    err.cause = networkErr;
    throw err;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    let message: string | undefined = error?.message;
    if (!message) {
      // Non-JSON response (proxy / gateway / cold start). Avoid leaking
      // browser status text like "Service Unavailable" — surface something
      // actionable instead.
      if (response.status === 503 || response.status === 504) {
        message = "Could not reach the server, please try again in a moment.";
      } else if (response.status === 502) {
        message = "The server is restarting, please try again in a moment.";
      } else if (response.status === 0) {
        message = "Network error — please check your connection and try again.";
      } else {
        message = `Request failed (HTTP ${response.status}). Please try again.`;
      }
    }
    const err: any = new Error(message);
    err.status = response.status;
    if (Array.isArray(error?.missingPermissionIds)) err.missingPermissionIds = error.missingPermissionIds;
    if (error?.conflict) err.conflict = error.conflict;
    if (error?.details) err.details = error.details;
    if (error?.code) err.code = error.code;
    // Receipt identifiers are supplied only after the server's access check.
    if (error?.blockingReceipt) err.blockingReceipt = error.blockingReceipt;
    if (error?.blockingReceiptId) err.blockingReceiptId = error.blockingReceiptId;
    if (error?.enforcement) {
      err.enforcement = error.enforcement;
    }
    if (error?.validationErrors) {
      err.validationErrors = error.validationErrors;
    }
    if (error?.error === "location_authorization") {
      err.error = "location_authorization";
      err.offendingItems = error.offending_items || [];
    }
    if (error?.error && !err.error) {
      err.error = error.error;
    }
    if (error?.referencedBy) {
      err.referencedBy = error.referencedBy;
    }
    throw err;
  }

  const data = await response.json();
  trackApiSuccess(endpoint, method, response.status, "online");
  // Cache successful GETs for offline reads.
  if (isGet && isCacheableGet(endpoint)) {
    void writeCachedResponse(endpoint, data, requestScope);
  }
  return data;
}

function safeParseBody(body: BodyInit | null | undefined): unknown {
  if (body == null) return undefined;
  if (typeof body === "string") {
    try { return JSON.parse(body); } catch { return body; }
  }
  return undefined;
}

// Register the replay function so the offline drain loop can re-issue
// queued mutations without re-queueing them.
registerReplayer(async (item: QueueItem) => {
  if (isPersonalWasteTemplateEndpoint(item.endpoint)) {
    return { ok: false as const, status: 409, message: "Waste templates cannot be synced offline. Please recreate this change while signed in." };
  }
  try {
    const wsId = item.workspaceId || (() => {
      try { return localStorage.getItem("currentWorkspaceId") || ""; } catch { return ""; }
    })();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (wsId && !item.endpoint.includes("workspaceId=")) {
      headers["x-workspace-id"] = wsId;
    }
    const res = await fetch(`${API_BASE}${item.endpoint}`, {
      method: item.method,
      headers,
      credentials: "include",
      body: item.body !== undefined ? safeStringify(item.body) : undefined,
    });
    if (!res.ok) {
      let msg = `Request failed (HTTP ${res.status}).`;
      try {
        const j = await res.json();
        if (j?.message) msg = j.message;
      } catch {}
      return { ok: false as const, status: res.status, message: msg };
    }
    trackApiSuccess(item.endpoint, item.method, res.status, "offline_sync");
    return { ok: true as const };
  } catch (e: any) {
    return { ok: false as const, status: 0, message: e?.message || "Network error" };
  }
});

export const api = {
  // Generic GET helper for bare endpoint paths (used by the sign-off
  // components and other call sites that pass a fully-formed path).
  get: (endpoint: string) => fetchAPI(endpoint),
  // Auth
  auth: {
    login: (email: string, password: string) =>
      fetchAPI("/auth/login", {
        method: "POST",
        body: safeStringify({ email, password }),
      }),
    logout: () =>
      fetchAPI("/auth/logout", {
        method: "POST",
      }),
    getUser: () => fetchAPI("/auth/user"),
    register: (data: { email: string; password: string; name: string; companyName: string }) =>
      fetchAPI("/register", {
        method: "POST",
        body: safeStringify(data),
      }),
    demoLogin: (role: string) =>
      fetchAPI("/auth/demo-login", {
        method: "POST",
        body: safeStringify({ role }),
      }),
    setActiveWorkspace: (workspaceId: string) =>
      fetchAPI("/auth/active-workspace", {
        method: "POST",
        body: safeStringify({ workspaceId }),
      }),
  },

  // Workspaces
  workspaces: {
    list: () => fetchAPI("/workspaces"),
    create: (data: { name: string; logo?: string }) =>
      fetchAPI("/workspaces", {
        method: "POST",
        body: safeStringify(data),
      }),
  },

  // Permissions
  permissions: {
    list: () => fetchAPI("/permissions"),
  },

  // Roles
  roles: {
    list: (workspaceId?: string) =>
      fetchAPI(`/roles${workspaceId ? `?workspaceId=${workspaceId}` : ""}`),
    get: (id: string) => fetchAPI(`/roles/${id}`),
    create: (data: { name: string; description: string; workspaceId?: string | null; isSystem: boolean; permissions: string[]; reason?: string; sourceRoleId?: string }) =>
      fetchAPI("/roles", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/roles/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string, reason?: string) =>
      fetchAPI(`/roles/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
        ...(reason?.trim() ? { body: safeStringify({ reason: reason.trim() }) } : {}),
      }),
  },

  // Users
  users: {
    list: (workspaceId: string) => fetchAPI(`/users?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/users", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/users/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/users/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
    resendWelcomeEmail: (id: string, workspaceId: string) =>
      fetchAPI(`/users/${id}/resend-welcome-email?workspaceId=${workspaceId}`, {
        method: "POST",
      }),
  },

  // Locations
  locations: {
    list: (workspaceId?: string) =>
      fetchAPI(`/locations${workspaceId ? `?workspaceId=${workspaceId}` : ""}`),
    create: (data: any) =>
      fetchAPI("/locations", {
        method: "POST",
        body: safeStringify(data),
      }),
    bulkCreate: (
      rows: Array<{ rowNumber: number; name: string; type: string; address: string; phone: string | null; prefix: string; customerId?: string | null; buid?: string | null; salesType?: string | null }>,
      workspaceId: string
    ): Promise<BranchBulkUploadResult> =>
      fetchAPI(`/locations/bulk?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ rows, workspaceId }),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/locations/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/locations/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Vehicles
  vehicles: {
    list: (workspaceId: string) => fetchAPI(`/vehicles?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/vehicles", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/vehicles/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    deactivate: (id: string, workspaceId: string) =>
      fetchAPI(`/vehicles/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Brands
  brands: {
    list: (workspaceId: string) => fetchAPI(`/brands?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/brands", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/brands/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/brands/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Categories
  categories: {
    list: (workspaceId: string, scope?: CategoryScope) =>
      fetchAPI(`/categories?workspaceId=${encodeURIComponent(workspaceId)}${scope ? `&scope=${encodeURIComponent(scope)}` : ""}`),
    create: (data: any) =>
      fetchAPI("/categories", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/categories/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/categories/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  subcategories: {
    list: (workspaceId: string, categoryId?: string, scope?: CategoryScope) =>
      fetchAPI(`/subcategories?workspaceId=${encodeURIComponent(workspaceId)}${scope ? `&scope=${encodeURIComponent(scope)}` : ""}${categoryId ? `&categoryId=${encodeURIComponent(categoryId)}` : ""}`),
    create: (data: any) =>
      fetchAPI("/subcategories", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/subcategories/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/subcategories/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Units
  units: {
    list: (workspaceId: string) => fetchAPI(`/units?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/units", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any, opts?: { force?: boolean }) =>
      fetchAPI(`/units/${id}${opts?.force ? "?force=true" : ""}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/units/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
    bulkUploadTemplateUrl: (workspaceId: string) =>
      `/api/units/bulk-upload/template?workspaceId=${workspaceId}`,
    bulkUploadParse: (workspaceId: string, csvText: string) =>
      fetchAPI(`/units/bulk-upload/parse?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ csvText }),
      }),
    bulkUploadConfirm: (workspaceId: string, csvText: string) =>
      fetchAPI(`/units/bulk-upload/confirm?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ csvText }),
      }),
  },

  // Items
  items: {
    list: (workspaceId: string, opts?: { activeOnly?: boolean }) =>
      fetchAPI(`/items?workspaceId=${workspaceId}${opts?.activeOnly ? "&includeInactive=false" : ""}`),
    purchasingCosts: (workspaceId: string): Promise<ItemPurchasingCost[]> =>
      fetchAPI(`/items/purchasing-costs?workspaceId=${workspaceId}`),
    get: (id: string) => fetchAPI(`/items/${id}`),
    getByBarcode: (code: string, workspaceId: string) =>
      fetchAPI(`/items/by-barcode/${encodeURIComponent(code)}?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/items", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/items/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/items/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
    disable: (id: string, workspaceId: string) =>
      fetchAPI(`/items/${id}/disable?workspaceId=${workspaceId}`, { method: "POST" }),
    enable: (id: string, workspaceId: string) =>
      fetchAPI(`/items/${id}/enable?workspaceId=${workspaceId}`, { method: "POST" }),
    restore: (id: string, workspaceId: string) =>
      fetchAPI(`/items/${id}/restore?workspaceId=${workspaceId}`, { method: "POST" }),
    bulkDisable: (itemIds: string[], workspaceId: string) =>
      fetchAPI(`/items/bulk/disable?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ itemIds }) }),
    bulkEnable: (itemIds: string[], workspaceId: string) =>
      fetchAPI(`/items/bulk/enable?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ itemIds }) }),
    bulkDelete: (itemIds: string[], workspaceId: string) =>
      fetchAPI(`/items/bulk/delete?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ itemIds }) }),
    getConversions: (itemId: string) =>
      fetchAPI(`/items/${itemId}/unit-conversions`),
  },

  // Stock
  stock: {
    list: (params: { workspaceId?: string; locationId?: string }) => {
      const query = new URLSearchParams(params as any).toString();
      return fetchAPI(`/stock?${query}`);
    },
    upsert: (data: any) =>
      fetchAPI("/stock", {
        method: "POST",
        body: safeStringify(data),
      }),
  },

  // Orders
  orders: {
    list: (opts: { workspaceId: string; page?: number; limit?: number; status?: string; branchId?: string; fromDate?: string; toDate?: string; forMe?: boolean; forApproverId?: string; sortBy?: string; sortDir?: string; itemId?: string }) => {
      const params = new URLSearchParams({ workspaceId: opts.workspaceId });
      if (opts.page != null) params.set("page", String(opts.page));
      if (opts.limit != null) params.set("limit", String(opts.limit));
      if (opts.status && opts.status !== "all") params.set("status", opts.status);
      if (opts.branchId && opts.branchId !== "all") params.set("branchId", opts.branchId);
      if (opts.fromDate) params.set("fromDate", opts.fromDate);
      if (opts.toDate) params.set("toDate", opts.toDate);
      if (opts.forMe) params.set("forMe", "1");
      if (opts.forApproverId) params.set("forApproverId", opts.forApproverId);
      if (opts.sortBy) params.set("sortBy", opts.sortBy);
      if (opts.sortDir) params.set("sortDir", opts.sortDir);
      if (opts.itemId) params.set("itemId", opts.itemId);
      return fetchAPI(`/orders?${params.toString()}`);
    },
    get: (id: string, workspaceId?: string) => fetchAPI(`/orders/${id}${workspaceId ? `?workspaceId=${workspaceId}` : ""}`),
    create: (data: any) =>
      fetchAPI("/orders", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/orders/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    cancel: (id: string, data: { reason: string; note?: string; reverseStock?: boolean }, workspaceId: string) =>
      fetchAPI(`/orders/${id}/cancel?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify(data),
      }),
    batchSuggestNames: (branchId: string, dates: string[], workspaceId: string) =>
      fetchAPI(`/orders/batch-suggest-names?branchId=${branchId}&dates=${dates.join(",")}&workspaceId=${workspaceId}`),
    batchCreate: (workspaceId: string, data: any) =>
      fetchAPI(`/orders/batch?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify(data),
      }),
    receive: (id: string, data: { lines: Array<{ itemId: string; orderItemId?: string | null; expectedQty?: number | null; receivedQty: number; unit: string; unitCost?: number | null; damaged?: boolean; notes?: string | null }>; notes?: string | null; receivedTemperatureC?: number | string | null; conditionNotes?: string | null }) =>
      fetchAPI(`/orders/${id}/receive`, {
        method: "POST",
        body: safeStringify(data),
      }),
    receipts: (id: string) => fetchAPI(`/orders/${id}/receipts`),
  },
  warehouseOrderReceipts: {
    get: (id: string) => fetchAPI(`/warehouse-order-receipts/${id}`),
  },

  // Templates
  templates: {
    list: (workspaceId: string) => fetchAPI(`/templates?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/templates", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any, workspaceId: string) =>
      fetchAPI(`/templates/${id}?workspaceId=${workspaceId}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/templates/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Branch Template Favorites
  branchTemplateFavorites: {
    list: (branchId: string, workspaceId: string) =>
      fetchAPI(`/branches/${branchId}/template-favorites?workspaceId=${workspaceId}`),
    add: (branchId: string, templateId: string, workspaceId: string) =>
      fetchAPI(`/branches/${branchId}/template-favorites?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ templateId }),
      }),
    remove: (branchId: string, templateId: string, workspaceId: string) =>
      fetchAPI(`/branches/${branchId}/template-favorites/${templateId}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
    reorder: (branchId: string, templateIds: string[], workspaceId: string) =>
      fetchAPI(`/branches/${branchId}/template-favorites/reorder?workspaceId=${workspaceId}`, {
        method: "PUT",
        body: safeStringify({ templateIds }),
      }),
    withFavorites: (workspaceId: string, branchId?: string) =>
      fetchAPI(`/templates/with-favorites?workspaceId=${workspaceId}${branchId ? `&branchId=${branchId}` : ''}`),
  },

  // Per-item history
  itemHistory: {
    transfers: (itemId: string, workspaceId: string) =>
      fetchAPI(`/items/${itemId}/transfers?workspaceId=${workspaceId}`),
    receipts: (itemId: string, workspaceId: string) =>
      fetchAPI(`/items/${itemId}/receipts?workspaceId=${workspaceId}`),
  },

  // Price History
  priceHistory: {
    list: (workspaceId: string) => fetchAPI(`/price-history?workspaceId=${workspaceId}`),
    get: (itemId: string, workspaceId: string) => fetchAPI(`/price-history/${itemId}?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/price-history", {
        method: "POST",
        body: safeStringify(data),
      }),
  },

  // Price Change Reasons
  priceChangeReasons: {
    list: (workspaceId: string) => fetchAPI(`/price-change-reasons?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/price-change-reasons", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/price-change-reasons/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/price-change-reasons/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Audit Log
  auditLog: {
    list: (workspaceId: string, options?: { entityType?: string; limit?: number }) => {
      const params = new URLSearchParams({ workspaceId });
      if (options?.entityType) params.append('entityType', options.entityType);
      if (options?.limit) params.append('limit', options.limit.toString());
      return fetchAPI(`/audit-log?${params.toString()}`);
    },
  },

  // Delivery Zones
  deliveryZones: {
    list: (workspaceId: string) => fetchAPI(`/delivery-zones?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI("/delivery-zones", {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any) =>
      fetchAPI(`/delivery-zones/${id}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/delivery-zones/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
      }),
  },

  // Transfers
  transfers: {
    list: (workspaceId: string, filters?: { status?: string; fromLocationId?: string; toLocationId?: string }) => {
      const params = new URLSearchParams({ workspaceId });
      if (filters?.status) params.append('status', filters.status);
      if (filters?.fromLocationId) params.append('fromLocationId', filters.fromLocationId);
      if (filters?.toLocationId) params.append('toLocationId', filters.toLocationId);
      return fetchAPI(`/transfers?${params.toString()}`);
    },
    get: (id: string, workspaceId: string) => fetchAPI(`/transfers/${id}?workspaceId=${workspaceId}`),
    sourceItems: (workspaceId: string, fromLocationId: string) => {
      const params = new URLSearchParams({ workspaceId, fromLocationId });
      return fetchAPI(`/transfers/source-items?${params}`);
    },
    create: (data: any) =>
      fetchAPI("/transfers", {
        method: "POST",
        body: safeStringify(data),
      }),
    suggestName: (fromLocationId: string, toLocationId: string, workspaceId: string) =>
      fetchAPI(`/transfers/suggest-name?fromLocationId=${fromLocationId}&toLocationId=${toLocationId}&workspaceId=${workspaceId}`),
    approve: (id: string, workspaceId: string) =>
      fetchAPI(`/transfers/${id}/approve?workspaceId=${workspaceId}`, { method: "POST" }),
    ship: (id: string, data: any) =>
      fetchAPI(`/transfers/${id}/ship`, {
        method: "POST",
        body: safeStringify(data),
      }),
    receive: (id: string, data: any) =>
      fetchAPI(`/transfers/${id}/receive`, {
        method: "POST",
        body: safeStringify(data),
      }),
    cancel: (id: string, data: any) =>
      fetchAPI(`/transfers/${id}/cancel`, {
        method: "POST",
        body: safeStringify(data),
      }),
  },

  // Workspace Settings
  workspaceSettings: {
    get: (key: string, workspaceId: string) =>
      fetchAPI(`/workspace-settings?key=${key}&workspaceId=${workspaceId}`),
    set: (key: string, value: string, workspaceId: string) =>
      fetchAPI("/workspace-settings", {
        method: "PUT",
        body: safeStringify({ key, value, workspaceId }),
      }),
  },

  // Warehouse Zone Lead Times
  warehouseZoneLeadTimes: {
    list: (workspaceId: string, warehouseId?: string) => {
      const params = new URLSearchParams({ workspaceId });
      if (warehouseId) params.append('warehouseId', warehouseId);
      return fetchAPI(`/warehouse-zone-lead-times?${params.toString()}`);
    },
    upsert: (data: any) =>
      fetchAPI("/warehouse-zone-lead-times", {
        method: "PUT",
        body: safeStringify(data),
      }),
    delete: (data: { warehouseId: string; zoneId: string; workspaceId: string }) =>
      fetchAPI(`/warehouse-zone-lead-times?workspaceId=${data.workspaceId}`, {
        method: "DELETE",
        body: safeStringify({ warehouseId: data.warehouseId, zoneId: data.zoneId }),
      }),
  },

  branchInventory: {
    list: async (locationId: string, search?: string) => {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await fetchAPI(`/locations/${locationId}/inventory${params}`);
      return res?.items ?? [];
    },
    listWithExternalStatus: async (
      locationId: string,
      search?: string,
    ): Promise<{
      items: any[];
      overrideStatus: ExternalInventoryOverrideStatus;
      overrideError: string | null;
    }> => {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await fetchAPI(
        `/locations/${locationId}/inventory${params}`,
      );
      return {
        items: res?.items ?? [],
        overrideStatus: res?.overrideStatus ?? "not_applicable",
        overrideError: res?.overrideError ?? null,
      };
    },
    summary: async (locationId: string) => {
      const res = await fetchAPI(`/locations/${locationId}/inventory/summary`);
      return res?.summary ?? res;
    },
    summaryWithExternalStatus: async (
      locationId: string,
    ): Promise<{
      summary: any;
      overrideStatus: ExternalInventoryOverrideStatus;
      overrideError: string | null;
    }> => {
      const res = await fetchAPI(
        `/locations/${locationId}/inventory/summary`,
      );
      return {
        summary: res?.summary ?? null,
        overrideStatus: res?.overrideStatus ?? "not_applicable",
        overrideError: res?.overrideError ?? null,
      };
    },
    transactions: (locationId: string, itemId: string, params?: { limit?: number; offset?: number }) => {
      const qs = params ? `?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined).map(([k,v]) => [k, String(v)]))).toString()}` : "";
      return fetchAPI(`/locations/${locationId}/inventory/${itemId}/transactions${qs}`);
    },
    pending: (locationId: string, itemId: string) =>
      fetchAPI(`/locations/${locationId}/inventory/${itemId}/pending`),
    adjust: (locationId: string, data: { itemId: string; quantity: number; note: string }) =>
      fetchAPI(`/locations/${locationId}/inventory/adjust`, {
        method: "POST",
        body: safeStringify(data),
      }),
    setSafetyStock: (locationId: string, itemId: string, value: number | null, workspaceId: string) =>
      fetchAPI(`/locations/${locationId}/inventory/${itemId}/safety-stock?workspaceId=${workspaceId}`, {
        method: "PUT",
        body: safeStringify({ safetyStockOverride: value, workspaceId }),
      }),
    setSafetyStockBulk: (
      locationId: string,
      updates: Array<{ itemId: string; value: number | null }>,
      workspaceId: string
    ) =>
      fetchAPI(`/locations/${locationId}/inventory/safety-stock/bulk?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ updates, workspaceId }),
      }),
    initialize: (
      locationId: string,
      itemIds: string[],
      workspaceId: string,
      safetyStockOverride?: number | null
    ) =>
      fetchAPI(`/locations/${locationId}/inventory/initialize?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ itemIds, safetyStockOverride, workspaceId }),
      }),
    uploadSafetyStocks: (
      locationId: string,
      rows: Array<{ rowNumber: number; sku: string; safetyStockOverride: number | null }>,
      workspaceId: string
    ): Promise<SafetyStockUploadResult> =>
      fetchAPI(`/locations/${locationId}/inventory/safety-stock/upload?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ rows, workspaceId }),
      }),
    bulkSetSafetyStock: (workspaceId: string, updates: { locationId: string; itemId: string; safetyStockOverride: number | null }[]) =>
      fetchAPI(`/inventory/safety-stock/bulk?workspaceId=${workspaceId}`, {
        method: "PUT",
        body: safeStringify({ workspaceId, updates }),
      }),
    uploadOpeningStock: (
      rows: Array<{ rowNumber: number; branch: string; sku: string; quantity: number | null; unitCost: number | null }>,
      mode: "skip_existing" | "replace",
      meta: { filename: string; fileFormat: "csv" | "xlsx"; fileSizeBytes: number },
      workspaceId: string
    ): Promise<OpeningStockUploadResult> =>
      fetchAPI(`/inventory/opening-stock/upload?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ rows, mode, ...meta, workspaceId }),
      }),
    lowStock: (workspaceId: string) => fetchAPI(`/inventory/low-stock?workspaceId=${workspaceId}`),
    safetyStockMatrix: (workspaceId: string) =>
      fetchAPI(`/inventory/safety-stocks/matrix?workspaceId=${workspaceId}`),
  },

  enforcement: {
    checkStatus: (locationId: string, action: "create_order" | "create_transfer", workspaceId: string) =>
      fetchAPI(`/locations/${locationId}/enforcement-status?action=${action}&workspaceId=${workspaceId}`),
    getSettings: (workspaceId: string) =>
      fetchAPI(`/admin/enforcement-settings?workspaceId=${workspaceId}`),
    updateSettings: (workspaceId: string, data: any) =>
      fetchAPI(`/admin/enforcement-settings?workspaceId=${workspaceId}`, {
        method: "PUT",
        body: safeStringify(data),
      }),
    getOverrides: (workspaceId: string) =>
      fetchAPI(`/admin/enforcement-overrides?workspaceId=${workspaceId}`),
  },

  branchProfile: {
    analytics: (locationId: string, workspaceId: string, lookback?: string, horizon?: number) => {
      const params = new URLSearchParams({ workspaceId });
      if (lookback) params.set("lookback", lookback);
      if (horizon) params.set("horizon", String(horizon));
      return fetchAPI(`/analytics/branch-profile/${locationId}?${params}`);
    },
  },

  demandEvents: {
    list: (workspaceId: string) =>
      fetchAPI(`/demand-events?workspaceId=${workspaceId}`),
    create: (data: any) =>
      fetchAPI(`/demand-events`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, data: any, workspaceId: string) =>
      fetchAPI(`/demand-events/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/demand-events/${id}?workspaceId=${workspaceId}`, { method: "DELETE" }),
  },

  stocktakes: {
    list: (workspaceId: string, filters?: { locationId?: string; status?: string; includeDeleted?: boolean }) => {
      const params = new URLSearchParams({ workspaceId });
      if (filters?.locationId) params.set("locationId", filters.locationId);
      if (filters?.status) params.set("status", filters.status);
      if (filters?.includeDeleted) params.set("includeDeleted", "1");
      return fetchAPI(`/stocktakes?${params}`);
    },
    get: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}?workspaceId=${workspaceId}`),
    // Mobile Phase 4: create/update accept optional `assignees` (user ids) and
    // `dueAt` (ISO datetime string) so the desktop form can route a count to a
    // specific team and post a soft due time the mobile rapid-count screen shows.
    create: (workspaceId: string, data: { locationId: string; name: string; notes?: string; assignees?: string[]; dueAt?: string | null }) =>
      fetchAPI(`/stocktakes?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: { name?: string; notes?: string; assignees?: string[] | null; dueAt?: string | null }) =>
      fetchAPI(`/stocktakes/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    // Mobile rapid-count list — returns only the user's assigned drafts.
    mine: (workspaceId: string) =>
      fetchAPI(`/me/stocktakes?workspaceId=${workspaceId}`),
    getItems: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/items?workspaceId=${workspaceId}`),
    enterCount: (id: string, itemId: string, workspaceId: string, data: { countedQuantity: number | null; notes?: string }) =>
      fetchAPI(`/stocktakes/${id}/items/${itemId}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    submit: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/submit?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    approve: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/approve?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    reject: (id: string, workspaceId: string, data: { reason: string }) =>
      fetchAPI(`/stocktakes/${id}/reject?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    reopen: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/reopen?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    auditLog: (id: string, workspaceId: string, opts?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams({ workspaceId });
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.offset) params.set("offset", String(opts.offset));
      return fetchAPI(`/stocktakes/${id}/audit-log?${params}`);
    },
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}?workspaceId=${workspaceId}`, { method: "DELETE" }),
    restore: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/restore?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    uploadPhoto: (id: string, workspaceId: string, data: { fileName: string; mimeType: string; photoData: string; qrCodes: { itemId: string; box: { vertices: { x: number; y: number }[] } }[] }) =>
      fetchAPI(`/stocktakes/${id}/photos?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    listPhotos: (id: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/photos?workspaceId=${workspaceId}`),
    applyPhoto: (id: string, photoId: string, workspaceId: string, accepted: { itemId: string; value: number }[]) =>
      fetchAPI(`/stocktakes/${id}/photos/${photoId}/apply?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ accepted }) }),
    deletePhoto: (id: string, photoId: string, workspaceId: string) =>
      fetchAPI(`/stocktakes/${id}/photos/${photoId}?workspaceId=${workspaceId}`, { method: "DELETE" }),
    photoImageUrl: (id: string, photoId: string, workspaceId: string) =>
      `/api/stocktakes/${id}/photos/${photoId}/image?workspaceId=${workspaceId}`,
  },

  itemConversions: {
    list: (itemId: string, workspaceId: string) =>
      fetchAPI(`/items/${itemId}/unit-conversions?workspaceId=${workspaceId}`),
    replace: (
      itemId: string,
      workspaceId: string,
      rows: Array<{ unitId: string; baseUnitsPer: number; displayOrder?: number }>,
    ) =>
      fetchAPI(`/items/${itemId}/unit-conversions?workspaceId=${workspaceId}`, {
        method: "PUT",
        body: safeStringify({ conversions: rows }),
      }),
    setDefaultDisplayUnit: (itemId: string, workspaceId: string, unitId: string | null) =>
      fetchAPI(`/items/${itemId}/default-display-unit?workspaceId=${workspaceId}`, {
        method: "PATCH",
        body: safeStringify({ defaultDisplayUnitId: unitId }),
      }),
    changeBaseUnit: (
      itemId: string,
      workspaceId: string,
      data: { newBaseUnitId: string; conversionFactor: number; reason: string },
    ) =>
      fetchAPI(`/items/${itemId}/base-unit-change?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify(data),
      }),
    baseUnitChanges: (itemId: string, workspaceId: string) =>
      fetchAPI(`/items/${itemId}/base-unit-changes?workspaceId=${workspaceId}`),

    bulkUploadTemplateUrl: (workspaceId: string) =>
      `/api/item-conversions/bulk-upload/template?workspaceId=${workspaceId}`,
    bulkUploadParse: (workspaceId: string, csv: string) =>
      fetchAPI(`/item-conversions/bulk-upload/parse?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ csvText: csv }),
      }),
    bulkUploadConfirm: (workspaceId: string, csv: string) =>
      fetchAPI(`/item-conversions/bulk-upload/confirm?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ csvText: csv }),
      }),
  },

  itemGroups: {
    list: (workspaceId: string) =>
      fetchAPI(`/item-groups?workspaceId=${workspaceId}`),
    get: (id: string, workspaceId: string) =>
      fetchAPI(`/item-groups/${id}?workspaceId=${workspaceId}`),
    create: (workspaceId: string, data: { name: string; description?: string }) =>
      fetchAPI(`/item-groups?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: { name?: string; description?: string }) =>
      fetchAPI(`/item-groups/${id}?workspaceId=${workspaceId}`, { method: "PATCH", body: safeStringify(data) }),
    delete: (id: string, workspaceId: string) =>
      fetchAPI(`/item-groups/${id}?workspaceId=${workspaceId}`, { method: "DELETE" }),
    addItems: (groupId: string, workspaceId: string, itemIds: string[]) =>
      fetchAPI(`/item-groups/${groupId}/items?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ itemIds }) }),
    removeItem: (groupId: string, itemId: string, workspaceId: string) =>
      fetchAPI(`/item-groups/${groupId}/items/${itemId}?workspaceId=${workspaceId}`, { method: "DELETE" }),
    listForLocation: (locationId: string, workspaceId: string) =>
      fetchAPI(`/locations/${locationId}/item-groups?workspaceId=${workspaceId}`),
    assignToLocation: (locationId: string, workspaceId: string, groupIds: string[]) =>
      fetchAPI(`/locations/${locationId}/item-groups?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ groupIds }) }),
    unassignFromLocation: (locationId: string, groupId: string, workspaceId: string) =>
      fetchAPI(`/locations/${locationId}/item-groups/${groupId}?workspaceId=${workspaceId}`, { method: "DELETE" }),

    getBranches: (groupId: string, workspaceId: string) =>
      fetchAPI(`/item-groups/${groupId}/branches?workspaceId=${workspaceId}`),

    bulkUploadTemplateUrl: (workspaceId: string) =>
      `/api/item-groups/bulk-upload/template?workspaceId=${workspaceId}`,
    bulkUploadParse: (workspaceId: string, csv: string) =>
      fetchAPI(`/item-groups/bulk-upload/parse?workspaceId=${workspaceId}`, {
        method: "POST",
        headers: { "Content-Type": "text/csv; charset=utf-8" },
        body: csv,
      }),
    bulkUploadConfirm: (workspaceId: string, csv: string) =>
      fetchAPI(`/item-groups/bulk-upload/confirm?workspaceId=${workspaceId}`, {
        method: "POST",
        headers: { "Content-Type": "text/csv; charset=utf-8" },
        body: csv,
      }),
  },

  recipes: {
    list: (workspaceId: string) => fetchAPI(`/recipes?workspaceId=${workspaceId}`),
    overview: (workspaceId: string, params: import("@shared/recipe-overview").RecipeOverviewQuery) => {
      const search = new URLSearchParams({ workspaceId });
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "" && value !== "all") search.set(key, String(value));
      });
      return fetchAPI(`/recipes/overview?${search.toString()}`);
    },
    draftQueue: (workspaceId: string) =>
      fetchAPI(`/recipes/drafts?workspaceId=${workspaceId}`),
    activateDrafts: (workspaceId: string, recipeIds: string[]) =>
      fetchAPI(`/recipes/bulk-activate?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ recipeIds }),
      }),
    discardDrafts: (workspaceId: string, recipeIds: string[]) =>
      fetchAPI(`/recipes/bulk-discard?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ recipeIds, onlyUnchanged: false }),
      }),
    get: (id: string, workspaceId: string) => fetchAPI(`/recipes/${id}?workspaceId=${workspaceId}`),
    ingredientReceipts: (
      recipeId: string,
      workspaceId: string,
      itemId: string,
      options?: { page?: number; pageSize?: number },
    ) => {
      const search = new URLSearchParams({
        workspaceId,
        itemId,
        page: String(options?.page ?? 1),
        pageSize: String(options?.pageSize ?? 10),
      });
      return fetchAPI(`/recipes/${encodeURIComponent(recipeId)}/ingredient-receipts?${search.toString()}`);
    },
    ingredientReplenishmentOrders: (
      recipeId: string,
      workspaceId: string,
      itemId: string,
      options?: { page?: number; pageSize?: number },
    ) => {
      const search = new URLSearchParams({
        workspaceId,
        itemId,
        page: String(options?.page ?? 1),
        pageSize: String(options?.pageSize ?? 10),
      });
      return fetchAPI(`/recipes/${encodeURIComponent(recipeId)}/ingredient-replenishment-orders?${search.toString()}`);
    },
    relatedDocuments: (
      recipeId: string,
      workspaceId: string,
      options?: {
        source?: "receipts" | "orders";
        itemId?: string;
        page?: number;
        pageSize?: number;
      },
    ) => {
      const search = new URLSearchParams({
        workspaceId,
        source: options?.source ?? "receipts",
        page: String(options?.page ?? 1),
        pageSize: String(options?.pageSize ?? 10),
      });
      if (options?.itemId) search.set("itemId", options.itemId);
      return fetchAPI(`/recipes/${encodeURIComponent(recipeId)}/related-documents?${search.toString()}`);
    },
    create: (workspaceId: string, data: any) => fetchAPI(`/recipes?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: any) => fetchAPI(`/recipes/${id}?workspaceId=${workspaceId}`, { method: "PATCH", body: safeStringify(data) }),
    createVersion: (id: string, workspaceId: string) => fetchAPI(`/recipes/${id}/new-version?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    activate: (id: string, workspaceId: string) => fetchAPI(`/recipes/${id}/activate?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    discard: (id: string, workspaceId: string) => fetchAPI(`/recipes/${id}/discard?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ onlyUnchanged: false }) }),
    cancelActivation: (id: string, workspaceId: string) => fetchAPI(`/recipes/${id}/cancel-activation?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    bulkPreview: (workspaceId: string, rows: Record<string, unknown>[]) =>
      fetchAPI(`/recipes/bulk-upload/preview?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ rows }) }),
    bulkConfirm: (workspaceId: string, rows: Record<string, unknown>[]) =>
      fetchAPI(`/recipes/bulk-upload/confirm?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ rows }) }),
  },

  countSchedules: {
    list: (workspaceId: string) =>
      fetchAPI(`/admin/count-schedules?workspaceId=${workspaceId}`),
    create: (workspaceId: string, data: any) =>
      fetchAPI(`/admin/count-schedules?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/admin/count-schedules/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    deactivate: (id: string, workspaceId: string) =>
      fetchAPI(`/admin/count-schedules/${id}?workspaceId=${workspaceId}`, { method: "DELETE" }),
    refresh: (id: string, workspaceId: string) =>
      fetchAPI(`/admin/count-schedules/${id}/refresh?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    branchStatus: (locationId: string, workspaceId: string) =>
      fetchAPI(`/locations/${locationId}/count-schedule-status?workspaceId=${workspaceId}`),
    getBranchStatus: (locationId: string, workspaceId: string) =>
      fetchAPI(`/locations/${locationId}/count-schedule-status?workspaceId=${workspaceId}`),
  },

  suppliers: {
    analytics: (workspaceId: string) =>
      fetchAPI(`/suppliers/analytics?workspaceId=${workspaceId}`),
    list: (workspaceId: string, params?: { category?: string; scope?: string; branchId?: string; status?: string; search?: string }) => {
      const q = new URLSearchParams({ workspaceId });
      if (params) {
        Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) q.set(k, v); });
      }
      return fetchAPI(`/suppliers?${q}`);
    },
    get: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}?workspaceId=${workspaceId}`),
    create: (workspaceId: string, data: any) =>
      fetchAPI(`/suppliers?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/suppliers/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    deactivate: (id: string, workspaceId: string, opts?: { reason?: string; force?: boolean }) =>
      fetchAPI(`/suppliers/${id}/deactivate?workspaceId=${workspaceId}${opts?.force ? "&force=true" : ""}`, { method: "PATCH", body: safeStringify({ reason: opts?.reason || null }) }),
    getItems: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}/items?workspaceId=${workspaceId}`),
    linkItem: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/suppliers/${id}/items?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    updateItem: (supplierId: string, supplierItemId: string, workspaceId: string, data: any) =>
      fetchAPI(`/suppliers/${supplierId}/items/${supplierItemId}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    unlinkItem: (supplierId: string, supplierItemId: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${supplierId}/items/${supplierItemId}?workspaceId=${workspaceId}`, { method: "DELETE" }),
    getItemPrices: (id: string, workspaceId: string, options?: { includeFallback?: boolean }): Promise<SupplierItemPrice[]> =>
      fetchAPI(`/suppliers/${id}/item-prices?workspaceId=${workspaceId}${options?.includeFallback ? "&includeFallback=true" : ""}`),
    getBalance: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}/balance?workspaceId=${workspaceId}`),
    getPayables: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}/payables?workspaceId=${workspaceId}`),
    getPayments: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}/payments?workspaceId=${workspaceId}&status=all`),
    voidPayment: (paymentId: string, workspaceId: string, reason: string, cashReturnConfirmed = false) =>
      fetchAPI(`/supplier-payments/${paymentId}/void?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ reason, cashReturnConfirmed }) }),
    confirmPayment: (paymentId: string, workspaceId: string) =>
      fetchAPI(`/supplier-payments/${paymentId}/confirm?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    getTerms: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}/terms?workspaceId=${workspaceId}`),
    updateTerms: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/suppliers/${id}/terms?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    getStatement: (id: string, workspaceId: string, dateFrom: string, dateTo: string): Promise<SupplierStatement> =>
      fetchAPI(`/suppliers/${id}/statement?workspaceId=${workspaceId}&dateFrom=${encodeURIComponent(dateFrom)}&dateTo=${encodeURIComponent(dateTo)}`),
    listChangeRequests: (id: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${id}/change-requests?workspaceId=${workspaceId}`),
    createChangeRequest: (id: string, workspaceId: string, data: { requesterNote: string; requestedChanges?: any }) =>
      fetchAPI(`/suppliers/${id}/change-requests?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    reviewChangeRequest: (requestId: string, workspaceId: string, data: { action: "approve" | "reject"; reviewerNote?: string }) =>
      fetchAPI(`/suppliers/change-requests/${requestId}/review?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    bulkLinkItems: (
      workspaceId: string,
      rows: Array<{ rowNumber: number; supplierId: string; itemId: string; unit: string; referencePrice?: number | null }>
    ): Promise<{ created: number; updated: number; skipped: number; errors: { row: number; reason: string }[] }> =>
      fetchAPI(`/suppliers/bulk-link-items?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ rows }),
      }),
  },

  supplierPayments: {
    list: (
      workspaceId: string,
      filters?: {
        supplierId?: string;
        dateFrom?: string;
        dateTo?: string;
        method?: string;
        hasUnallocated?: boolean;
        status?: string;
      },
    ) => {
      const query = new URLSearchParams({ workspaceId });
      if (filters?.supplierId) query.set("supplierId", filters.supplierId);
      if (filters?.dateFrom) query.set("dateFrom", filters.dateFrom);
      if (filters?.dateTo) query.set("dateTo", filters.dateTo);
      if (filters?.method) query.set("method", filters.method);
      if (filters?.hasUnallocated) query.set("hasUnallocated", "true");
      if (filters?.status) query.set("status", filters.status);
      return fetchAPI(`/supplier-payments?${query}`);
    },
    get: (paymentId: string, workspaceId: string) =>
      fetchAPI(`/supplier-payments/${paymentId}?workspaceId=${workspaceId}`),
    create: (workspaceId: string, data: {
      idempotencyKey: string;
      supplierId: string;
      paymentDate: string;
      amount: string;
      method: string;
      cleared?: boolean;
      reference?: string | null;
      notes?: string | null;
      attachmentUrl?: string | null;
      attachmentName?: string | null;
      allocations?: Array<{ receiptId: string; amount: string }>;
    }) =>
      fetchAPI(`/supplier-payments?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify(data),
      }),
    bulkPreview: (workspaceId: string, csv: string, importId: string) =>
      fetchAPI(`/supplier-payments/bulk/preview?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ csv, importId }),
      }) as Promise<SupplierPaymentBulkPreview>,
    bulkConfirm: (workspaceId: string, csv: string, importId: string, previewToken: string) =>
      fetchAPI(`/supplier-payments/bulk/confirm?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ csv, importId, previewToken }),
      }) as Promise<SupplierPaymentBulkResult>,
  },

  purchaseOrders: {
    list: (workspaceId: string, params?: { branchId?: string; supplierId?: string; status?: string; preset?: string; paginated?: string; page?: string; pageSize?: string; search?: string; createdBy?: string; createdFrom?: string; createdTo?: string; deliveryFrom?: string; deliveryTo?: string; itemId?: string }) => {
      const clean = Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined));
      const q = new URLSearchParams({ workspaceId, ...clean });
      return fetchAPI(`/purchase-orders?${q}`);
    },
    get: (id: string, workspaceId: string) =>
      fetchAPI(`/purchase-orders/${id}?workspaceId=${workspaceId}`, { cache: "no-store" }),
    receivedBeforeApprovalCount: (workspaceId: string) =>
      fetchAPI(`/purchase-orders/received-before-approval-count?workspaceId=${workspaceId}`, { cache: "no-store" }),
    markReceivedReviewed: (id: string, workspaceId: string, data: { note?: string; expectedRevision: number }) =>
      fetchAPI(`/purchase-orders/${id}/mark-received-reviewed?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    create: (workspaceId: string, data: any, clientRequestId?: string) =>
      fetchAPI(`/purchase-orders?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ ...data, ...(clientRequestId ? { clientRequestId } : {}) }),
      }),
    update: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/purchase-orders/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    send: (id: string, workspaceId: string, expectedRevision?: number) =>
      fetchAPI(`/purchase-orders/${id}/send?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ expectedRevision }) }),
    acknowledge: (id: string, workspaceId: string, expectedRevision?: number) =>
      fetchAPI(`/purchase-orders/${id}/acknowledge?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ expectedRevision }) }),
    cancel: (id: string, workspaceId: string, data: { reason: string; expectedRevision?: number }) =>
      fetchAPI(`/purchase-orders/${id}/cancel?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    whatsappMessage: (id: string, workspaceId: string, lang: string) =>
      fetchAPI(`/purchase-orders/${id}/whatsapp-message?workspaceId=${workspaceId}&lang=${lang}`),
    submit: (id: string, workspaceId: string, expectedRevision?: number) =>
      fetchAPI(`/purchase-orders/${id}/submit?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ expectedRevision }) }),
    procurementReview: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/purchase-orders/${id}/procurement-review?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    financeReview: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/purchase-orders/${id}/finance-review?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    sendToSupplier: (id: string, workspaceId: string, data?: any) =>
      fetchAPI(`/purchase-orders/${id}/send-to-supplier?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data || {}) }),
    dispute: (id: string, workspaceId: string, message: string, expectedRevision?: number) =>
      fetchAPI(`/purchase-orders/${id}/dispute?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ message, expectedRevision }) }),
    resubmit: (id: string, workspaceId: string, expectedRevision?: number) =>
      fetchAPI(`/purchase-orders/${id}/resubmit?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ expectedRevision }) }),
  },

  supplierReceipts: {
    brandOptions: (workspaceId: string, scope: { itemId: string; supplierId: string; branchId: string; poLineId?: string }): Promise<ReceiptBrandOptions> =>
      fetchAPI(`/supplier-receipts/brand-options?${new URLSearchParams({ workspaceId, ...scope })}`),
    listPage: (workspaceId: string, filters: Record<string, string | number | boolean | undefined>) => {
      const q = new URLSearchParams({ workspaceId, page: "1", pageSize: "20" });
      Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== "" && value !== "all") q.set(key, String(value)); });
      return fetchAPI(`/supplier-receipts?${q}`);
    },
    listChangesPage: (workspaceId: string, filters: Record<string, string | number | boolean | undefined>) => {
      const q = new URLSearchParams({ workspaceId, page: "1", pageSize: "20" });
      Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== "" && value !== "all") q.set(key, String(value)); });
      return fetchAPI(`/supplier-receipts/changes?${q}`);
    },
    events: (id: string, workspaceId: string, offset = 0) =>
      fetchAPI(`/supplier-receipts/${id}/events?${new URLSearchParams({ workspaceId, limit: "20", offset: String(offset) })}`),
    finalizePrices: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/${id}/finalize-prices?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    updatePaymentTerms: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/${id}/payment-terms?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    withdrawCancellation: (id: string, requestId: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/${id}/cancellation-requests/${requestId}/withdraw?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    eligiblePurchaseOrders: (workspaceId: string, supplierId: string, branchId?: string) =>
      fetchAPI(`/supplier-receipts/purchase-orders?${new URLSearchParams({ workspaceId, supplierId, ...(branchId ? { branchId } : {}) })}`),
    eligiblePurchaseOrder: (id: string, workspaceId: string, branchId?: string) =>
      fetchAPI(`/supplier-receipts/purchase-orders/${encodeURIComponent(id)}?${new URLSearchParams({ workspaceId, ...(branchId ? { branchId } : {}) })}`),
    list: (workspaceId: string, params?: { branchId?: string; supplierId?: string; status?: string; poId?: string; paymentMethod?: string; paymentStatus?: string; pricingStatus?: string; offTerms?: boolean }) => {
      const clean = Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined));
      const q = new URLSearchParams({ workspaceId, ...clean });
      return fetchAPI(`/supplier-receipts?${q}`);
    },
    get: (id: string, workspaceId: string) =>
      fetchAPI(`/supplier-receipts/${id}?workspaceId=${workspaceId}`),
    create: async (workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(await checkReceiptBeforeWrite(workspaceId, data)) }),
    update: async (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(await checkReceiptBeforeWrite(workspaceId, data, id)) }),
    confirm: async (id: string, workspaceId: string, data: SupplierReceiptConfirmation) =>
      fetchAPI(`/supplier-receipts/${id}/confirm?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(await checkReceiptBeforeWrite(workspaceId, data, id, true)) }),
    cancelDraft: (id: string, workspaceId: string, data: { category: string; reasonText?: string; expectedRevision?: number }) =>
      fetchAPI(`/supplier-receipts/${id}/cancel?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    createCancellationRequest: (id: string, workspaceId: string, requesterNote: string, expectedRevision?: number) =>
      fetchAPI(`/supplier-receipts/${id}/cancellation-requests?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify({ requesterNote, expectedRevision }),
      }),
    priceCheck: (workspaceId: string, receiptId: string) =>
      fetchAPI(`/supplier-receipts/price-check?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ receiptId }) }),
    qualityReview: (workspaceId: string, params?: { from?: string; to?: string; branchId?: string }) => {
      const query = new URLSearchParams({ workspaceId });
      if (params?.from) query.set("from", params.from);
      if (params?.to) query.set("to", params.to);
      if (params?.branchId && params.branchId !== "all") query.set("branchId", params.branchId);
      return fetchAPI(`/supplier-receipts/quality-review?${query.toString()}`);
    },
    pendingPricing: (workspaceId: string, params?: { supplierId?: string; branchId?: string; from?: string; to?: string; pricing?: string; period?: string }) => {
      const query = new URLSearchParams({ workspaceId });
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value && value !== "all") query.set(key, value);
      });
      return fetchAPI(`/supplier-receipts/pending-pricing?${query.toString()}`);
    },
    previewPendingPricing: (workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/pending-pricing/preview?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    applyPendingPricing: (workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/pending-pricing/apply?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    editConfirmed: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-receipts/${id}/edit-confirmed?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    listChangeRequests: (id: string, workspaceId: string) =>
      fetchAPI(`/supplier-receipts/${id}/change-requests?workspaceId=${workspaceId}`),
    createChangeRequest: (id: string, workspaceId: string, data: { requesterNote?: string; requestedChanges?: any; expectedRevision?: number }) =>
      fetchAPI(`/supplier-receipts/${id}/change-requests?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    reviewChangeRequest: (requestId: string, workspaceId: string, data: { action: "approve" | "reject"; reviewerNote?: string; expectedRevision?: number }) =>
      fetchAPI(`/supplier-receipts/change-requests/${requestId}/review?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    listChanges: (workspaceId: string, status?: string) => {
      const q = new URLSearchParams({ workspaceId });
      if (status) q.set("status", status);
      return fetchAPI(`/supplier-receipt-changes?${q}`);
    },
  },

  supplierTerms: {
    resolve: (supplierId: string, locationId: string, workspaceId: string) =>
      fetchAPI(`/suppliers/${supplierId}/terms/resolve?workspaceId=${workspaceId}&locationId=${encodeURIComponent(locationId)}`),
  },

  procurementSettings: {
    get: (workspaceId: string) =>
      fetchAPI(`/procurement-settings?workspaceId=${workspaceId}`),
    update: (workspaceId: string, data: any) =>
      fetchAPI(`/procurement-settings?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
  },

  reports: {
    ingredientsReview: (
      workspaceId: string,
      params?: {
        search?: string;
        page?: number;
        pageSize?: number;
        filter?: "all" | "no_document_history" | "price_review";
      },
    ) => {
      const query = new URLSearchParams({ workspaceId });
      if (params?.search) query.set("search", params.search);
      if (params?.page != null) query.set("page", String(params.page));
      if (params?.pageSize != null) query.set("pageSize", String(params.pageSize));
      if (params?.filter && params.filter !== "all") query.set("filter", params.filter);
      return fetchAPI(`/reports/ingredients-review?${query.toString()}`);
    },
    transactions: (workspaceId: string, params: { dateFrom: string; dateTo: string; transactionTypes?: string; orderStatuses?: string; locationId?: string; itemId?: string; page?: number; pageSize?: number }) => {
      const clean: Record<string, string> = { workspaceId };
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") clean[k] = String(v); });
      const q = new URLSearchParams(clean);
      return fetchAPI(`/reports/transactions?${q}`);
    },
    transactionsExportUrl: (workspaceId: string, params: { dateFrom: string; dateTo: string; transactionTypes?: string; orderStatuses?: string; locationId?: string; itemId?: string }) => {
      const clean: Record<string, string> = { workspaceId };
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") clean[k] = String(v); });
      const q = new URLSearchParams(clean);
      return `/api/reports/transactions/export?${q}`;
    },
    transactionsExportSummaryUrl: (workspaceId: string, params: { dateFrom: string; dateTo: string; transactionTypes?: string; orderStatuses?: string; locationId?: string; itemId?: string }) => {
      const clean: Record<string, string> = { workspaceId };
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") clean[k] = String(v); });
      const q = new URLSearchParams(clean);
      return `/api/reports/transactions/export-summary?${q}`;
    },
  },

  returns: {
    list: (workspaceId: string, params?: { returnType?: string; status?: string; fromLocationId?: string; toLocationId?: string; supplierId?: string }) => {
      const q = new URLSearchParams({ workspaceId, ...params });
      return fetchAPI(`/returns?${q}`);
    },
    get: (id: string, workspaceId: string) => fetchAPI(`/returns/${id}?workspaceId=${workspaceId}`),
    sourceItems: (workspaceId: string, fromLocationId: string) => {
      const q = new URLSearchParams({ workspaceId, fromLocationId });
      return fetchAPI(`/returns/source-items?${q}`);
    },
    create: (workspaceId: string, data: any) =>
      fetchAPI(`/returns?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/returns/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    updateValues: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/returns/${id}/values?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    submit: (id: string, workspaceId: string) =>
      fetchAPI(`/returns/${id}/submit?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    approve: (id: string, workspaceId: string) =>
      fetchAPI(`/returns/${id}/approve?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    reject: (id: string, workspaceId: string, rejectionReason: string) =>
      fetchAPI(`/returns/${id}/reject?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ rejectionReason }) }),
    ship: (id: string, workspaceId: string, items: { itemId: string; shippedQty: number }[]) =>
      fetchAPI(`/returns/${id}/ship?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ items }) }),
    receive: (id: string, workspaceId: string, items: { itemId: string; receivedQty: number; disposition: string; dispositionNote?: string }[]) =>
      fetchAPI(`/returns/${id}/receive?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ items }) }),
    close: (id: string, workspaceId: string) =>
      fetchAPI(`/returns/${id}/close?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    resolveQuarantine: (returnId: string, itemId: string, workspaceId: string, data: { disposition: string; dispositionNote?: string }) =>
      fetchAPI(`/returns/${returnId}/items/${itemId}/resolve-quarantine?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    approveWriteOff: (returnId: string, itemId: string, workspaceId: string) =>
      fetchAPI(`/returns/${returnId}/items/${itemId}/approve-write-off?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    quarantine: (workspaceId: string, locationId?: string) => {
      const q = new URLSearchParams({ workspaceId, ...(locationId ? { locationId } : {}) });
      return fetchAPI(`/returns/quarantine?${q}`);
    },
    stats: (workspaceId: string, params?: { locationId?: string; dateFrom?: string; dateTo?: string }) => {
      const q = new URLSearchParams({ workspaceId, ...params });
      return fetchAPI(`/returns/stats?${q}`);
    },
    settings: {
      get: (workspaceId: string) => fetchAPI(`/return-settings?workspaceId=${workspaceId}`),
      update: (workspaceId: string, data: any) =>
        fetchAPI(`/return-settings?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    },
  },

  supplierCycles: {
    list: (workspaceId: string, params?: { supplierId?: string; status?: string }) => {
      const q = new URLSearchParams({ workspaceId, ...params });
      return fetchAPI(`/supplier-cycles?${q}`);
    },
    get: (id: string, workspaceId: string) =>
      fetchAPI(`/supplier-cycles/${id}?workspaceId=${workspaceId}`),
    create: (workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycles?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    update: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycles/${id}?workspaceId=${workspaceId}`, { method: "PUT", body: safeStringify(data) }),
    deactivate: (id: string, workspaceId: string) =>
      fetchAPI(`/supplier-cycles/${id}/deactivate?workspaceId=${workspaceId}`, { method: "PATCH", body: safeStringify({}) }),
    listInstances: (workspaceId: string, params?: { cycleId?: string; status?: string }) => {
      const q = new URLSearchParams({ workspaceId, ...params });
      return fetchAPI(`/supplier-cycle-instances?${q}`);
    },
    getInstance: (id: string, workspaceId: string) =>
      fetchAPI(`/supplier-cycle-instances/${id}?workspaceId=${workspaceId}`),
    overrideDeadline: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycle-instances/${id}/override-deadline?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    skipInstance: (id: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycle-instances/${id}/skip?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    getMySubmission: (instanceId: string, workspaceId: string, branchId: string) =>
      fetchAPI(`/supplier-cycle-instances/${instanceId}/my-submission?workspaceId=${workspaceId}&branchId=${branchId}`),
    submit: (instanceId: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycle-instances/${instanceId}/submit?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    skipBranch: (instanceId: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycle-instances/${instanceId}/skip-branch?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    saveDraft: (instanceId: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycle-instances/${instanceId}/save-draft?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    generateLink: (instanceId: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-cycle-instances/${instanceId}/generate-link?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    listShareLinks: (instanceId: string, workspaceId: string) =>
      fetchAPI(`/supplier-cycle-instances/${instanceId}/share-links?workspaceId=${workspaceId}`),
    revokeLink: (linkId: string, workspaceId: string) =>
      fetchAPI(`/supplier-share-links/${linkId}/revoke?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    getLinkResponses: (linkId: string, workspaceId: string) =>
      fetchAPI(`/supplier-share-links/${linkId}/responses?workspaceId=${workspaceId}`),
    shareLinkWhatsapp: (linkId: string, workspaceId: string, lang: string) =>
      fetchAPI(`/supplier-share-links/${linkId}/whatsapp-message?workspaceId=${workspaceId}&lang=${lang}`),
    acceptAdjustment: (responseId: string, workspaceId: string, expectedRevision?: number) =>
      fetchAPI(`/supplier-po-responses/${responseId}/accept-adjustment?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({ expectedRevision }) }),
    rejectAdjustment: (responseId: string, workspaceId: string) =>
      fetchAPI(`/supplier-po-responses/${responseId}/reject-adjustment?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify({}) }),
    getIssueResolutions: (workspaceId: string, params?: { status?: string; instanceId?: string }) => {
      const q = new URLSearchParams({ workspaceId, ...params });
      return fetchAPI(`/supplier-issue-resolutions?${q}`);
    },
    resolveIssue: (resolutionId: string, workspaceId: string, data: any) =>
      fetchAPI(`/supplier-issue-resolutions/${resolutionId}/resolve?workspaceId=${workspaceId}`, { method: "POST", body: safeStringify(data) }),
    generateInstances: (workspaceId: string) =>
      fetchAPI(`/admin/generate-cycle-instances?workspaceId=${workspaceId}`),
    getPortalData: (token: string) =>
      fetchAPI(`/public/supplier-portal/${token}`, { cache: "no-store" }),
    respondPortal: (token: string, data: { poId: string; status: string; comment?: string; adjustedLines?: any[]; expectedRevision: number }) =>
      fetchAPI(`/public/supplier-portal/${token}/respond`, { method: "POST", body: safeStringify(data) }),
  },

  auditLogs: {
    list: (workspaceId: string, params?: {
      limit?: number; offset?: number; entityType?: string; entityId?: string;
      userId?: string; action?: string; from?: string; to?: string; search?: string;
    }) => {
      const q = new URLSearchParams({ workspaceId });
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.offset) q.set("offset", String(params.offset));
      if (params?.entityType) q.set("entityType", params.entityType);
      if (params?.entityId) q.set("entityId", params.entityId);
      if (params?.userId) q.set("userId", params.userId);
      if (params?.action) q.set("action", params.action);
      if (params?.from) q.set("from", params.from);
      if (params?.to) q.set("to", params.to);
      if (params?.search) q.set("search", params.search);
      return fetchAPI(`/audit-logs?${q}`);
    },
    stats: (workspaceId: string) =>
      fetchAPI(`/audit-logs/stats?workspaceId=${workspaceId}`),
    byEntity: (workspaceId: string, entityType: string, entityId: string, params?: { limit?: number; offset?: number }) => {
      const q = new URLSearchParams({ workspaceId });
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.offset) q.set("offset", String(params.offset));
      return fetchAPI(`/audit-logs/entity/${entityType}/${entityId}?${q}`);
    },
  },

  // Customers (Koning Food Industries only)
  customers: {
    list: (workspaceId: string) => fetchAPI(`/customers?workspaceId=${workspaceId}`),
    get: (id: string, workspaceId: string) => fetchAPI(`/customers/${id}?workspaceId=${workspaceId}`),
    create: (data: any, workspaceId: string) =>
      fetchAPI(`/customers?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify(data),
      }),
    update: (id: string, data: any, workspaceId: string) =>
      fetchAPI(`/customers/${id}?workspaceId=${workspaceId}`, {
        method: "PATCH",
        body: safeStringify(data),
      }),
    remove: (id: string, workspaceId: string, opts?: { reason?: string }) =>
      fetchAPI(`/customers/${id}?workspaceId=${workspaceId}`, {
        method: "DELETE",
        body: safeStringify({ reason: opts?.reason || null }),
      }),
  },

  // Customer Orders (Koning Food Industries only)
  customerOrders: {
    list: (workspaceId: string) => fetchAPI(`/customer-orders?workspaceId=${workspaceId}`),
    get: (id: string, workspaceId: string) => fetchAPI(`/customer-orders/${id}?workspaceId=${workspaceId}`),
    create: (data: any, workspaceId: string) =>
      fetchAPI(`/customer-orders?workspaceId=${workspaceId}`, {
        method: "POST",
        body: safeStringify(data),
      }),
  },
};
